import { useCallback, useRef } from 'react';
import { Box, Popover, SpaceBetween, StatusIndicator } from '@cloudscape-design/components';
import { formatSourceName } from '../common/formatting/sourceFormat';

const VIOLATION_TYPE_LABELS: Record<string, string> = {
  fake_engagement: 'Fake engagement',
  harassment: 'Harassment',
  hate_speech: 'Hate speech',
  offensive_content: 'Offensive content',
  personal_information: 'Personal information',
  off_topic: 'Off-topic',
  spam: 'Spam',
  obscenity: 'Obscenity',
  conflict_of_interest: 'Conflict of interest',
  privacy_violation: 'Privacy violation',
  hearsay: 'Hearsay',
  ai_generated: 'AI-generated',
};

export type ViolationBadgeProps = {
  violations: { violation_type: string; violation_reason: string }[];
  source: string | null | undefined;
};

export function ViolationBadge({ violations, source }: ViolationBadgeProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hoverOpenedRef = useRef(false);

  // With triggerType="text", Cloudscape renders a <button> with class
  // "awsui_trigger_" as the click target. onClick always calls
  // setVisible(true) (never toggles), so repeated clicks are safe.
  const getTrigger = useCallback(
    () =>
      wrapperRef.current?.querySelector('[class*="awsui_trigger_"]') as HTMLElement | null,
    [],
  );

  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(() => {
      if (hoverOpenedRef.current) {
        // Cloudscape Popover closes on Escape keydown on the trigger
        const trigger = getTrigger();
        if (trigger) {
          trigger.dispatchEvent(new KeyboardEvent('keydown', { keyCode: 27, bubbles: true }));
        }
        hoverOpenedRef.current = false;
      }
    }, 300);
  }, [getTrigger]);

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
  }, []);

  // After the popover opens via hover, attach mouseover/mouseout listeners
  // on the portal-rendered popover body so hovering the body keeps it open.
  const attachBodyListeners = useCallback(() => {
    requestAnimationFrame(() => {
      const bodies = document.querySelectorAll('[class*="awsui_container_"][class*="awsui_refresh_"]');
      const body = bodies[bodies.length - 1];
      if (body) {
        body.addEventListener('mouseenter', cancelClose);
        body.addEventListener('mouseleave', scheduleClose);
      }
    });
  }, [cancelClose, scheduleClose]);

  if (violations.length === 0) return null;

  const sourceName = formatSourceName(source);
  const label =
    violations.length === 1
      ? `Possible ${sourceName} policy violation`
      : `${violations.length} possible ${sourceName} policy violations`;

  const handleMouseEnter = () => {
    cancelClose();
    hoverOpenedRef.current = true;
    const trigger = getTrigger();
    if (trigger) {
      trigger.click();
      attachBodyListeners();
    }
  };

  const handleMouseLeave = () => {
    scheduleClose();
  };

  return (
    <Box margin={{ top: 'xxs' }}>
      <div
        ref={wrapperRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Popover
          header="Policy violations"
          content={
            <SpaceBetween direction="vertical" size="xxs">
              {violations.map((v) => (
                <Box key={v.violation_type}>
                  <Box variant="span" fontWeight="bold">
                    {VIOLATION_TYPE_LABELS[v.violation_type] ?? v.violation_type}
                  </Box>
                  {': '}
                  <Box variant="span">{v.violation_reason}</Box>
                </Box>
              ))}
            </SpaceBetween>
          }
          triggerType="text"
          size="large"
          renderWithPortal
        >
          <StatusIndicator type="warning">{label}</StatusIndicator>
        </Popover>
      </div>
    </Box>
  );
}
